import { useState, useEffect, useRef } from 'react'
import * as api from '../../lib/api'
import { proxyStorageUrl } from '../../lib/supabase'
import './PinLockScreen.css'

export default function PinLockScreen({ profile, onUnlock, onLogout }) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [shake, setShake] = useState(false)
  const [loading, setLoading] = useState(false)
  const [locked, setLocked] = useState(false)
  const [lockTimer, setLockTimer] = useState(0)
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryAnswer, setRecoveryAnswer] = useState('')
  const [newPin, setNewPin] = useState('')
  const [newPinConfirm, setNewPinConfirm] = useState('')
  const [recoveryStep, setRecoveryStep] = useState('answer') // 'answer', 'newpin'
  const [recoverySaving, setRecoverySaving] = useState(false)
  const inputRef = useRef(null)

  // Авто-фокус (только на десктопе, так как на мобильных фокус скрытого инпута ломает интерфейс/клавиатуру в Safari)
  useEffect(() => {
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (!isMobile && !showRecovery && inputRef.current) {
      try {
        inputRef.current.focus()
      } catch (e) {
        console.warn('Failed to focus hidden input:', e)
      }
    }
  }, [showRecovery])

  // Таймер блокировки
  useEffect(() => {
    if (lockTimer <= 0) {
      setLocked(false)
      return
    }
    const interval = setInterval(() => {
      setLockTimer(prev => {
        if (prev <= 1) {
          setLocked(false)
          setError('')
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [lockTimer])

  // Проверка PIN при вводе 4-й цифры
  useEffect(() => {
    if (pin.length === 4 && !loading && !locked) {
      verifyPin()
    }
  }, [pin])

  const verifyPin = async () => {
    setLoading(true)
    setError('')
    try {
      const result = await api.verifyPin(profile.id, pin)
      if (result.success) {
        if (document.activeElement) document.activeElement.blur();
        window.scrollTo(0, 0);
        onUnlock()
      } else {
        setShake(true)
        setTimeout(() => setShake(false), 600)
        setPin('')
        if (result.locked) {
          setLocked(true)
          setLockTimer(result.remainSeconds || 300)
          setError('Слишком много попыток. Подождите.')
        } else {
          setError(`Неверный PIN (осталось ${result.attemptsLeft} попыт${result.attemptsLeft === 1 ? 'ка' : 'ки'})`)
        }
      }
    } catch (err) {
      setError('Ошибка проверки PIN')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (digit) => {
    if (locked || loading) return
    if (pin.length < 4) {
      setPin(prev => prev + digit)
      setError('')
    }
  }

  const handleBackspace = () => {
    if (locked || loading) return
    setPin(prev => prev.slice(0, -1))
    setError('')
  }

  const handleRecoverySubmit = async () => {
    if (recoveryStep === 'answer') {
      setRecoverySaving(true)
      try {
        const isCorrect = await api.verifySecretAnswer(profile.id, recoveryAnswer)
        if (isCorrect) {
          setRecoveryStep('newpin')
          setError('')
        } else {
          setError('Неверный ответ')
        }
      } catch (err) {
        setError('Ошибка проверки')
      } finally {
        setRecoverySaving(false)
      }
    } else if (recoveryStep === 'newpin') {
      if (newPin.length !== 4) {
        setError('PIN должен быть 4 цифры')
        return
      }
      if (newPin !== newPinConfirm) {
        setError('PIN-коды не совпадают')
        return
      }
      setRecoverySaving(true)
      try {
        await api.setPin(profile.id, newPin)
        onUnlock()
      } catch (err) {
        setError('Ошибка сброса PIN')
      } finally {
        setRecoverySaving(false)
      }
    }
  }

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const initials = profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'

  // Восстановление PIN
  if (showRecovery) {
    return (
      <div className="pin-lock-overlay">
        <div className="pin-lock-container animate-scale-in">
          <div className="pin-lock-recovery">
            <button className="pin-lock-back" onClick={() => { setShowRecovery(false); setRecoveryStep('answer'); setError('') }}>
              ← Назад
            </button>
            <h2 className="pin-lock-title">Восстановление PIN</h2>

            {recoveryStep === 'answer' && (
              <>
                <div className="pin-lock-question">
                  {profile?.secret_question || 'Секретный вопрос не задан'}
                </div>
                {!profile?.secret_question ? (
                  <div className="pin-lock-hint">
                    Вы не настроили секретный вопрос. Обратитесь к администратору или выйдите из аккаунта.
                  </div>
                ) : (
                  <>
                    <input
                      className="pin-lock-text-input"
                      type="text"
                      autoComplete="off"
                      placeholder="Ваш ответ..."
                      value={recoveryAnswer}
                      onChange={e => setRecoveryAnswer(e.target.value)}
                    />
                    <button
                      className="pin-lock-btn-primary"
                      disabled={!recoveryAnswer.trim() || recoverySaving}
                      onClick={handleRecoverySubmit}
                    >
                      {recoverySaving ? <span className="btn-spinner-inline" style={{width:'14px',height:'14px'}} /> : 'Проверить'}
                    </button>
                  </>
                )}
              </>
            )}

            {recoveryStep === 'newpin' && (
              <>
                <div className="pin-lock-hint">Ответ верный! Задайте новый PIN-код.</div>
                <input
                  className="pin-lock-text-input pin-input-centered"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  autoComplete="off"
                  placeholder="Новый PIN"
                  value={newPin}
                  onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
                <input
                  className="pin-lock-text-input pin-input-centered"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  autoComplete="off"
                  placeholder="Повторите PIN"
                  value={newPinConfirm}
                  onChange={e => setNewPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
                <button
                  className="pin-lock-btn-primary"
                  disabled={newPin.length !== 4 || newPin !== newPinConfirm || recoverySaving}
                  onClick={handleRecoverySubmit}
                >
                  {recoverySaving ? <span className="btn-spinner-inline" style={{width:'14px',height:'14px'}} /> : 'Сохранить и войти'}
                </button>
              </>
            )}

            {error && <div className="pin-lock-error">{error}</div>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pin-lock-overlay">
      <div className="pin-lock-bg">
        <div className="pin-lock-bg-circle pin-lock-bg-circle-1"></div>
        <div className="pin-lock-bg-circle pin-lock-bg-circle-2"></div>
      </div>
      
      <div className={`pin-lock-container animate-scale-in ${shake ? 'pin-shake' : ''}`}>
        {/* Аватар */}
        <div className="pin-lock-avatar">
          {profile?.avatar_url ? (
            <img src={proxyStorageUrl(profile.avatar_url)} alt="" />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="pin-lock-name">{profile?.full_name}</div>
        <div className="pin-lock-subtitle">Введите PIN-код</div>

        {/* Точки */}
        <div className="pin-lock-dots">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={`pin-dot ${i < pin.length ? 'filled' : ''} ${loading && i < pin.length ? 'pulse' : ''}`} />
          ))}
        </div>

        {/* Ошибка */}
        {error && <div className="pin-lock-error">{error}</div>}

        {/* Таймер блокировки */}
        {locked && (
          <div className="pin-lock-timer">
            Заблокировано: {formatTime(lockTimer)}
          </div>
        )}

        {/* Цифровая клавиатура */}
        <div className="pin-numpad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
            <button key={d} className="pin-key" onClick={() => handleKeyPress(String(d))} disabled={locked || loading}>
              {d}
            </button>
          ))}
          <button className="pin-key pin-key-action" onClick={() => setShowRecovery(true)} disabled={loading}>
            <span style={{ fontSize: '13px' }}>Забыли?</span>
          </button>
          <button className="pin-key" onClick={() => handleKeyPress('0')} disabled={locked || loading}>
            0
          </button>
          <button className="pin-key pin-key-action" onClick={handleBackspace} disabled={locked || loading || pin.length === 0}>
            ⌫
          </button>
        </div>

        {/* Скрытый input для физической клавиатуры */}
        <input
          ref={inputRef}
          className="pin-hidden-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 4)
            if (!locked && !loading) setPin(val)
          }}
          onKeyDown={e => {
            if (e.key === 'Backspace') handleBackspace()
          }}
        />

        {/* Выход */}
        <button className="pin-lock-logout" onClick={onLogout}>
          Выйти из аккаунта
        </button>
      </div>
    </div>
  )
}
